// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The Satellites layer on a browser globe: one MapLibre custom layer per
 * map, drawing every shown object as a dot at the point under it, a
 * fading trail behind it, and the selected object's ground track.
 *
 * Positions go to the GPU as raw Earth-fixed km (the engine's arrays,
 * uploaded as they are), and the vertex shader projects them: onto
 * MapLibre's unit sphere through `mainMatrix` on the globe (as
 * earthTileLayer draws its sphere), or into the flat map's world square,
 * x = (lon + 180) / 360, y = (90 − lat) / 360.
 *
 * Buffers, with n objects and K trail slots:
 * - positions: (K + 1) × n × 3, trail slots then the current positions;
 * - flags: (K + 1) × n, per vertex, for the segment that ends there:
 *   1 = its start is in the ring, 2 = it doesn't cross the antimeridian;
 * - a style texture, one texel per object: colour, and in alpha the kind
 *   (0 hidden, 1 satellite, 2 station, 3 selected, 4 debris, 5 a source
 *   of the data on the globe — DSA 1.10 platforms, owner 2026-10-23 —
 *   drawn with a pulse and a halo from `u_time`).
 * The flags are `flat` varyings: GLSL takes them from a line's last
 * vertex, which is the segment's end.
 */
import type { CustomLayerInterface, CustomRenderMethodInput, Map as MapLibreMap } from 'maplibre-gl'
import { mod, TRAIL_SLOTS, TRAIL_STEP_S } from './satelliteEngine'
import type { SatelliteLayer } from './satelliteLayer'
import { isDebris } from './satelliteLayer'
import { logger } from '../../utils/logger'
import { GLOBE_RADIUS_KM } from './satelliteOrbit'
import { shellRadius } from '../vrSatellites'

export const SATELLITE_MAP_LAYER_ID = 'ayni-satellites'
const TEX_W = 1024
const K = TRAIL_SLOTS

const VERT = `#version 300 es
precision highp float;
precision highp int;
uniform mat4 u_matrix;
uniform vec4 u_clip;
uniform float u_flat;
uniform highp sampler2D u_style;
uniform int u_n;
uniform float u_ages[${K + 1}];
uniform float u_span;
uniform float u_px;
uniform int u_mode;
uniform vec4 u_track;
uniform float u_time;
// 0: on the ground; 1: true height; 2: compact (vrSatellites.shellRadius).
uniform int u_height;
// The camera in MapLibre's unit-sphere frame, from its clipping plane.
uniform vec3 u_cam;
in vec3 a_pos;
in float a_ok;
out vec4 v_colour;
out float v_kind;
flat out float v_ok;

vec4 project(vec3 p) {
  float r = length(p);
  vec3 u = p / r;
  if (u_flat > 0.5) {
    float lon = atan(u.y, u.x);
    float lat = asin(clamp(u.z, -1.0, 1.0));
    vec2 w = vec2((lon + 3.14159265) / 6.2831853, (1.5707963 - lat) / 6.2831853);
    return u_matrix * vec4(w, 0.0, 1.0);
  }
  // Earth-fixed (x to 0°E, y to 90°E, z north) to MapLibre's sphere:
  // x = cos(lat)·sin(lon), y = sin(lat), z = cos(lat)·cos(lon).
  vec3 s = vec3(u.y, u.z, u.x);
  float k = 1.0;
  if (u_height == 1) k = r / ${GLOBE_RADIUS_KM.toFixed(1)};
  else if (u_height == 2) k = 1.0 + 0.6 * sqrt(max(0.0, r - ${GLOBE_RADIUS_KM.toFixed(1)}) / 35786.0);
  vec3 e = s * k;
  vec4 pos = u_matrix * vec4(e, 1.0);
  if (k <= 1.0001) {
    // On the ground: MapLibre's own horizon clip (globeComputeClippingZ).
    pos.z = (1.0 - (dot(s, u_clip.xyz) + u_clip.w)) * pos.w;
    return pos;
  }
  // Above the ground: hidden only where the Earth stands between it and the
  // camera. Depth is not tested in this layer, so z only has to stay inside
  // the clip volume (beyond the far plane is fine for a satellite beside the globe).
  vec3 v = e - u_cam;
  float t = clamp(-dot(u_cam, v) / dot(v, v), 0.0, 1.0);
  vec3 c = u_cam + t * v;
  bool hidden = t < 1.0 && dot(c, c) < 1.0;
  pos.z = hidden ? 2.0 * pos.w : 0.0;
  return pos;
}

void main() {
  v_ok = 1.0;
  v_kind = 1.0;
  if (u_mode == 2) {
    v_colour = u_track;
    gl_Position = any(isnan(a_pos)) ? vec4(2.0, 2.0, 2.0, 1.0) : project(a_pos);
    return;
  }
  int idx = gl_VertexID % u_n;
  int slot = gl_VertexID / u_n;
  vec4 st = texelFetch(u_style, ivec2(idx % ${TEX_W}, idx / ${TEX_W}), 0);
  float kind = floor(st.a * 255.0 + 0.5);
  v_kind = kind;
  if (kind < 0.5 || any(isnan(a_pos))) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    v_colour = vec4(0.0);
    v_ok = 0.0;
    return;
  }
  gl_Position = project(a_pos);
  if (u_mode == 0) {
    // A source of the data (5) gets room for its halo.
    float size = kind > 5.5 ? 2.6 : kind > 4.5 ? 5.1 : kind > 3.5 ? 0.7 : kind > 2.5 ? 2.6 : kind > 1.5 ? 2.1 : 1.0;
    gl_PointSize = u_px * size;
    v_colour = vec4(kind > 2.5 && kind < 3.5 ? vec3(1.0) : st.rgb, 1.0);
  } else {
    float age = u_ages[slot];
    float a = clamp(1.0 - age / u_span, 0.0, 1.0);
    v_colour = vec4(st.rgb, a * a * (kind > 3.5 && kind < 4.5 ? 0.45 : 0.85));
    float okPrev = mod(a_ok, 2.0) >= 1.0 ? 1.0 : 0.0;
    float okSeam = a_ok >= 2.0 ? 1.0 : 0.0;
    v_ok = u_flat > 0.5 ? okPrev * okSeam : okPrev;
  }
}
`

const FRAG = `#version 300 es
precision mediump float;
precision highp int;
uniform int u_mode;
in vec4 v_colour;
in float v_kind;
uniform float u_time;
flat in float v_ok;
out vec4 fragColor;
void main() {
  if (v_ok < 0.5) discard;
  if (u_mode == 0) {
    vec2 c = gl_PointCoord * 2.0 - 1.0;
    float r = length(c);
    if (r > 1.0) discard;
    if (v_kind > 5.5) {
      // Feeds a dataset of the catalog (owner, 2026-10-24): its own dot inside a steady fuchsia ring.
      float core = 1.0 - smoothstep(0.34, 0.44, r);
      float ring = smoothstep(0.62, 0.70, r) * (1.0 - smoothstep(0.86, 0.96, r));
      vec3 col = mix(v_colour.rgb, vec3(0.03, 0.04, 0.06), smoothstep(0.22, 0.34, r)) * core + vec3(1.0, 0.0, 0.8) * ring;
      float a = max(core, ring * 0.95);
      fragColor = vec4(col, a);
      return;
    }
    if (v_kind > 4.5) {
      // A source of the data: a bright core and a halo that swells and fades (1.6 s).
      float pulse = fract(u_time * 0.625);
      float ring = 0.42 + 0.56 * pulse;
      float halo = (1.0 - smoothstep(0.0, 0.09, abs(r - ring))) * (1.0 - pulse);
      float core = 1.0 - smoothstep(0.30, 0.42, r);
      vec3 col = mix(vec3(1.0, 0.0, 0.8), vec3(1.0, 0.7, 1.0), 0.45 * (1.0 - pulse));
      col = mix(col, vec3(0.03, 0.04, 0.06), smoothstep(0.22, 0.30, r) * core * 0.6);
      float a = max(core, halo * 0.9);
      fragColor = vec4(col * a, a);
      return;
    }
    float a = 1.0 - smoothstep(0.86, 1.0, r);
    // A dark rim keeps the dot readable over white cloud and ice.
    vec3 col = mix(v_colour.rgb, vec3(0.03, 0.04, 0.06), smoothstep(0.52, 0.72, r));
    // Stations and the selected object: a hollow ring around a bright core.
    if (v_kind > 1.5 && v_kind < 3.5) {
      float ring = smoothstep(0.30, 0.38, r) * (1.0 - smoothstep(0.50, 0.58, r));
      col = mix(col, vec3(0.03, 0.04, 0.06), ring);
    }
    fragColor = vec4(col * a, a);
    return;
  }
  fragColor = vec4(v_colour.rgb * v_colour.a, v_colour.a);
}
`

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) ?? 'shader')
  return sh
}

/** True when a (lon) step between two Earth-fixed points crosses ±180°. */
function crossesSeam(ax: number, ay: number, bx: number, by: number): boolean {
  // Both west of ±180 but on opposite sides of it: x < 0 and y changes sign.
  return (ax < 0 || bx < 0) && ay * by < 0 && Math.abs(Math.atan2(ay, ax) - Math.atan2(by, bx)) > Math.PI
}

export interface SatelliteMapLayerOptions {
  layer: SatelliteLayer
  isFlat: () => boolean
  /**
   * Drawn and pickable while this says so; by default while the layer is on
   * and the main globe shows the Earth (`layer.shown`). A layout's other
   * globes pass their own body's answer (satelliteMapUI.ts).
   */
  shown?: () => boolean
}

export interface SatelliteMapLayer extends CustomLayerInterface {
  /** Screen-space pick: the shown object nearest (within `radiusPx`) to a point, or null. */
  pick(map: MapLibreMap, x: number, y: number, radiusPx?: number): number | null
  /** Where object i is drawn on this map, CSS px from its top-left, or null when hidden. */
  screenOf(map: MapLibreMap, i: number): { x: number; y: number } | null
}

export function createSatelliteMapLayer(opts: SatelliteMapLayerOptions): SatelliteMapLayer {
  const { layer, isFlat } = opts
  const shown = opts.shown ?? (() => layer.shown)
  const engine = layer.engine
  let map: MapLibreMap | null = null
  let prog: WebGLProgram | null = null
  let vao: WebGLVertexArrayObject | null = null
  let trackVao: WebGLVertexArrayObject | null = null
  let posBuf: WebGLBuffer | null = null
  let okBuf: WebGLBuffer | null = null
  let trailIdx: WebGLBuffer | null = null
  let headIdx: WebGLBuffer | null = null
  let trackBuf: WebGLBuffer | null = null
  let trackIdx: WebGLBuffer | null = null
  let tex: WebGLTexture | null = null
  const loc: Record<string, WebGLUniformLocation | null> = {}
  let n = 0
  let layoutSeen = -1
  let styleSeen = -1
  let flatSeen: boolean | null = null
  let headNewest = -2
  let ok = new Float32Array(0)
  let trackSeen: unknown = null
  let trackCount = 0
  let fovVao: WebGLVertexArrayObject | null = null
  let fovBuf: WebGLBuffer | null = null
  let fovSeen = -1
  let fovFlatSeen: boolean | null = null
  let fovCount = 0
  let lastClip: [number, number, number, number] = [0, 0, 0, 0]
  let lastCam: [number, number, number] = [0, 0, 1e6]
  let lastMatrix: Float64Array | null = null
  const reader = {}
  let offFrame: (() => void) | null = null

  function rebuildLayout(g: WebGL2RenderingContext): void {
    n = engine.count
    layoutSeen = engine.layoutVersion
    styleSeen = -1
    headNewest = -2
    flatSeen = null
    ok = new Float32Array((K + 1) * n)
    g.bindBuffer(g.ARRAY_BUFFER, posBuf)
    g.bufferData(g.ARRAY_BUFFER, Math.max(1, (K + 1) * n * 3) * 4, g.DYNAMIC_DRAW)
    g.bindBuffer(g.ARRAY_BUFFER, okBuf)
    g.bufferData(g.ARRAY_BUFFER, Math.max(1, ok.length) * 4, g.DYNAMIC_DRAW)
    const idx = new Uint32Array(K * n * 2)
    let o = 0
    for (let s = 0; s < K; s++) {
      const p = mod(s - 1, K)
      for (let i = 0; i < n; i++) {
        idx[o++] = p * n + i
        idx[o++] = s * n + i
      }
    }
    g.bindBuffer(g.ELEMENT_ARRAY_BUFFER, trailIdx)
    g.bufferData(g.ELEMENT_ARRAY_BUFFER, idx, g.STATIC_DRAW)
    // Every slot gets uploaded again on the next frame.
    engine.releaseReader(reader)
  }

  function uploadStyle(g: WebGL2RenderingContext): void {
    styleSeen = engine.styleVersion
    const h = Math.max(1, Math.ceil(n / TEX_W))
    const data = new Uint8Array(TEX_W * h * 4)
    const sel = layer.selected
    for (let i = 0; i < n; i++) {
      const info = engine.infos[i]
      data[i * 4] = Math.round(engine.colour[i * 3] * 255)
      data[i * 4 + 1] = Math.round(engine.colour[i * 3 + 1] * 255)
      data[i * 4 + 2] = Math.round(engine.colour[i * 3 + 2] * 255)
      data[i * 4 + 3] = !engine.mask[i] ? 0 : i === sel ? 3 : layer.isSource(i) ? 5 : layer.isCatalogSource(i) ? 6 : info.station ? 2 : isDebris(info) ? 4 : 1
    }
    g.bindTexture(g.TEXTURE_2D, tex)
    // MapLibre leaves its own unpack state (flipped, premultiplied) behind;
    // either would scramble this lookup table.
    g.pixelStorei(g.UNPACK_FLIP_Y_WEBGL, false)
    g.pixelStorei(g.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
    g.pixelStorei(g.UNPACK_ALIGNMENT, 4)
    g.texImage2D(g.TEXTURE_2D, 0, g.RGBA8, TEX_W, h, 0, g.RGBA, g.UNSIGNED_BYTE, data)
  }

  /** Flags of the segment ending at slot s (s = K: the head, from the newest slot). */
  function slotFlags(s: number, prevSlot: number, prevOk: boolean): void {
    const pos = s === K ? engine.current : engine.trail
    const base = s === K ? 0 : s * n * 3
    const pBase = prevSlot * n * 3
    const tr = engine.trail
    for (let i = 0; i < n; i++) {
      const a = base + i * 3
      const b = pBase + i * 3
      const x = pos[a]
      const px = tr[b]
      if (!prevOk || x !== x || px !== px) {
        ok[s * n + i] = 0
        continue
      }
      ok[s * n + i] = 1 + (crossesSeam(px, tr[b + 1], x, pos[a + 1]) ? 0 : 2)
    }
  }

  function uploadTrail(g: WebGL2RenderingContext): void {
    const dirty = engine.takeDirtySlots(reader)
    const flat = isFlat()
    const all = flatSeen !== flat
    flatSeen = flat
    const slots = all ? Array.from({ length: K }, (_, s) => s) : dirty
    if (!slots.length) return
    g.bindBuffer(g.ARRAY_BUFFER, posBuf)
    for (const s of dirty)
      g.bufferSubData(g.ARRAY_BUFFER, s * n * 3 * 4, engine.trail.subarray(s * n * 3, (s + 1) * n * 3))
    for (const s of slots) slotFlags(s, mod(s - 1, K), engine.slotHasPrevious(s))
    g.bindBuffer(g.ARRAY_BUFFER, okBuf)
    for (const s of slots) g.bufferSubData(g.ARRAY_BUFFER, s * n * 4, ok.subarray(s * n, (s + 1) * n))
  }

  function uploadHead(g: WebGL2RenderingContext): void {
    g.bindBuffer(g.ARRAY_BUFFER, posBuf)
    g.bufferSubData(g.ARRAY_BUFFER, K * n * 3 * 4, engine.current)
    const newest = engine.newestSlot()
    slotFlags(K, Math.max(0, newest), newest >= 0)
    g.bindBuffer(g.ARRAY_BUFFER, okBuf)
    g.bufferSubData(g.ARRAY_BUFFER, K * n * 4, ok.subarray(K * n, (K + 1) * n))
    if (newest !== headNewest) {
      headNewest = newest
      const idx = new Uint32Array(n * 2)
      for (let i = 0; i < n; i++) {
        idx[i * 2] = Math.max(0, newest) * n + i
        idx[i * 2 + 1] = K * n + i
      }
      g.bindBuffer(g.ELEMENT_ARRAY_BUFFER, headIdx)
      g.bufferData(g.ELEMENT_ARRAY_BUFFER, idx, g.DYNAMIC_DRAW)
    }
  }

  function uploadTrack(g: WebGL2RenderingContext): void {
    const orbit = layer.selectedOrbit
    // On the ground: its track over the ground; floating: its orbit, a closed ring.
    const floating = heightCode() !== 0
    const key = orbit ? `${orbit.id}:${isFlat()}:${floating}` : null
    if (key === trackSeen) return
    trackSeen = key
    trackCount = 0
    if (!orbit) return
    const line = floating ? orbit.ellipse : orbit.track
    const marks = orbit.marks ?? new Float32Array(0)
    const pts = new Float32Array(line.length + marks.length)
    pts.set(line)
    pts.set(marks, line.length)
    const count = line.length / 3
    const flat = isFlat()
    const idx: number[] = []
    for (let s = 1; s < count; s++) {
      const a = (s - 1) * 3
      const b = s * 3
      if (pts[a] !== pts[a] || pts[b] !== pts[b]) continue
      if (flat && crossesSeam(pts[a], pts[a + 1], pts[b], pts[b + 1])) continue
      idx.push(s - 1, s)
    }
    if (floating && count > 2 && pts[0] === pts[0] && pts[(count - 1) * 3] === pts[(count - 1) * 3]) idx.push(count - 1, 0)
    // The ring and pin where an object came down: pairs of points, each its own segment.
    for (let m = 0; m + 1 < marks.length / 3; m += 2) {
      const a = (count + m) * 3
      const b = (count + m + 1) * 3
      if (flat && crossesSeam(pts[a], pts[a + 1], pts[b], pts[b + 1])) continue
      idx.push(count + m, count + m + 1)
    }
    g.bindVertexArray(trackVao)
    g.bindBuffer(g.ARRAY_BUFFER, trackBuf)
    g.bufferData(g.ARRAY_BUFFER, pts, g.DYNAMIC_DRAW)
    g.bindBuffer(g.ELEMENT_ARRAY_BUFFER, trackIdx)
    g.bufferData(g.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), g.DYNAMIC_DRAW)
    g.bindVertexArray(vao)
    trackCount = idx.length
  }

  /** What the sources and the selected object see: the layer's footprints, uploaded when they change. */
  function uploadFootprints(g: WebGL2RenderingContext): void {
    const flat = isFlat()
    if (layer.footprintsVersion === fovSeen && fovFlatSeen === flat) return
    fovSeen = layer.footprintsVersion
    fovFlatSeen = flat
    const pts = layer.footprints
    fovCount = 0
    if (!pts.length) return
    // On the flat map a segment across the seam is dropped (its points set to NaN, which the shader hides).
    let data = pts
    if (flat) {
      data = new Float32Array(pts)
      for (let o = 0; o + 5 < data.length; o += 6) {
        if (crossesSeam(data[o], data[o + 1], data[o + 3], data[o + 4])) data[o] = data[o + 3] = NaN
      }
    }
    g.bindVertexArray(fovVao)
    g.bindBuffer(g.ARRAY_BUFFER, fovBuf)
    g.bufferData(g.ARRAY_BUFFER, data, g.DYNAMIC_DRAW)
    g.bindVertexArray(vao)
    fovCount = data.length / 3
  }

  const impl: SatelliteMapLayer = {
    id: SATELLITE_MAP_LAYER_ID,
    type: 'custom',
    renderingMode: '2d',

    onAdd(m: MapLibreMap, g: WebGL2RenderingContext) {
      map = m
      try {
        build(g)
      } catch (err) {
        // MapLibre drops a failed custom layer without a word: say why.
        logger.error('[satellites] map layer could not start:', err)
        prog = null
        return
      }
      offFrame = layer.onFrame(() => map?.triggerRepaint())
    },

    onRemove(_m: MapLibreMap, g: WebGL2RenderingContext) {
      onRemove(g)
    },

    render(g: WebGL2RenderingContext, args: CustomRenderMethodInput) {
      render(g, args)
    },

    pick(m: MapLibreMap, x: number, y: number, radiusPx = 10): number | null {
      return pick(m, x, y, radiusPx)
    },

    screenOf(m: MapLibreMap, i: number) {
      const cur = engine.current
      return screenOfPoint(m, cur[i * 3], cur[i * 3 + 1], cur[i * 3 + 2])
    },
  }

  /** 0 on the ground (and always on the flat map), 1 true height, 2 compact. */
  function heightCode(): number {
    if (isFlat()) return 0
    const h = layer.settings.mapHeight
    return h === 'true' ? 1 : h === 'compact' ? 2 : 0
  }

  /** The shader's projection on the CPU: CSS px, or null when hidden by the Earth or behind the camera. */
  function screenOfPoint(m: MapLibreMap, px: number, py: number, pz: number): { x: number; y: number } | null {
    const r = Math.hypot(px, py, pz)
    if (!(r > 0)) return null
    const code = heightCode()
    if (code === 0 || !lastMatrix) {
      // On the ground: MapLibre's horizon test, then its own projection.
      if (!isFlat() && (py / r) * lastClip[0] + (pz / r) * lastClip[1] + (px / r) * lastClip[2] + lastClip[3] < 0) {
        return null
      }
      const p = m.project([(Math.atan2(py, px) * 180) / Math.PI, (Math.asin(pz / r) * 180) / Math.PI])
      return { x: p.x, y: p.y }
    }
    const k = code === 1 ? r / GLOBE_RADIUS_KM : shellRadius(r - GLOBE_RADIUS_KM, 'compact')
    const e0 = (py / r) * k
    const e1 = (pz / r) * k
    const e2 = (px / r) * k
    const [c0, c1, c2] = lastCam
    const v0 = e0 - c0
    const v1 = e1 - c1
    const v2 = e2 - c2
    const t = Math.min(1, Math.max(0, -(c0 * v0 + c1 * v1 + c2 * v2) / (v0 * v0 + v1 * v1 + v2 * v2)))
    const q0 = c0 + t * v0
    const q1 = c1 + t * v1
    const q2 = c2 + t * v2
    if (t < 1 && q0 * q0 + q1 * q1 + q2 * q2 < 1) return null
    const M = lastMatrix
    const cx = M[0] * e0 + M[4] * e1 + M[8] * e2 + M[12]
    const cy = M[1] * e0 + M[5] * e1 + M[9] * e2 + M[13]
    const cw = M[3] * e0 + M[7] * e1 + M[11] * e2 + M[15]
    if (cw <= 0) return null
    const canvas = m.getCanvas()
    return { x: ((cx / cw + 1) / 2) * canvas.clientWidth, y: ((1 - cy / cw) / 2) * canvas.clientHeight }
  }

  function build(g: WebGL2RenderingContext): void {
    const p = g.createProgram()!
    g.attachShader(p, compile(g, g.VERTEX_SHADER, VERT))
    g.attachShader(p, compile(g, g.FRAGMENT_SHADER, FRAG))
    g.bindAttribLocation(p, 0, 'a_pos')
    g.bindAttribLocation(p, 1, 'a_ok')
    g.linkProgram(p)
    if (!g.getProgramParameter(p, g.LINK_STATUS)) throw new Error(g.getProgramInfoLog(p) ?? 'link')
    prog = p
    for (const u of [
      'u_matrix',
      'u_clip',
      'u_flat',
      'u_style',
      'u_n',
      'u_ages',
      'u_span',
      'u_px',
      'u_mode',
      'u_track',
      'u_height',
      'u_cam',
      'u_time',
    ]) {
      loc[u] = g.getUniformLocation(p, u)
    }
    posBuf = g.createBuffer()
    okBuf = g.createBuffer()
    trailIdx = g.createBuffer()
    headIdx = g.createBuffer()
    trackBuf = g.createBuffer()
    trackIdx = g.createBuffer()
    vao = g.createVertexArray()
    g.bindVertexArray(vao)
    g.bindBuffer(g.ARRAY_BUFFER, posBuf)
    g.enableVertexAttribArray(0)
    g.vertexAttribPointer(0, 3, g.FLOAT, false, 0, 0)
    g.bindBuffer(g.ARRAY_BUFFER, okBuf)
    g.enableVertexAttribArray(1)
    g.vertexAttribPointer(1, 1, g.FLOAT, false, 0, 0)
    g.bindVertexArray(null)
    fovVao = g.createVertexArray()
    g.bindVertexArray(fovVao)
    fovBuf = g.createBuffer()
    g.bindBuffer(g.ARRAY_BUFFER, fovBuf)
    g.bufferData(g.ARRAY_BUFFER, 4, g.DYNAMIC_DRAW)
    g.enableVertexAttribArray(0)
    g.vertexAttribPointer(0, 3, g.FLOAT, false, 0, 0)
    trackVao = g.createVertexArray()
    g.bindVertexArray(trackVao)
    g.bindBuffer(g.ARRAY_BUFFER, trackBuf)
    g.enableVertexAttribArray(0)
    g.vertexAttribPointer(0, 3, g.FLOAT, false, 0, 0)
    g.disableVertexAttribArray(1)
    g.vertexAttrib1f(1, 3)
    g.bindVertexArray(null)
    tex = g.createTexture()
    g.bindTexture(g.TEXTURE_2D, tex)
    g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MIN_FILTER, g.NEAREST)
    g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MAG_FILTER, g.NEAREST)
    g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_S, g.CLAMP_TO_EDGE)
    g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_T, g.CLAMP_TO_EDGE)
    layoutSeen = -1
  }

  function onRemove(g: WebGL2RenderingContext): void {
    offFrame?.()
    offFrame = null
    engine.releaseReader(reader)
    for (const b of [posBuf, okBuf, trailIdx, headIdx, trackBuf, trackIdx]) if (b) g.deleteBuffer(b)
    if (vao) g.deleteVertexArray(vao)
    if (trackVao) g.deleteVertexArray(trackVao)
    if (fovVao) g.deleteVertexArray(fovVao)
    if (fovBuf) g.deleteBuffer(fovBuf)
    fovVao = fovBuf = null
    fovSeen = -1
    if (tex) g.deleteTexture(tex)
    if (prog) g.deleteProgram(prog)
    prog = vao = trackVao = tex = null
    map = null
  }

  function render(g: WebGL2RenderingContext, args: CustomRenderMethodInput): void {
    if (!prog || !shown() || !engine.count) return
    // Element-buffer binds below land in our own VAO, never in MapLibre's
    // (MapLibre marks its GL state dirty after a custom layer).
    g.bindVertexArray(vao)
    if (engine.layoutVersion !== layoutSeen) rebuildLayout(g)
    if (engine.styleVersion !== styleSeen) uploadStyle(g)
    const trails = layer.settings.trails
    uploadTrail(g)
    uploadHead(g)
    uploadTrack(g)

    const pd = args.defaultProjectionData
    lastClip = [pd.clippingPlane[0], pd.clippingPlane[1], pd.clippingPlane[2], pd.clippingPlane[3]]
    // The plane faces the camera at 1/distance from the centre (MapLibre's _computeClippingPlane).
    const camDist = lastClip[3] < 0 ? -1 / lastClip[3] : 1e6
    lastCam = [lastClip[0] * camDist, lastClip[1] * camDist, lastClip[2] * camDist]
    lastMatrix = Float64Array.from(pd.mainMatrix as ArrayLike<number>)
    g.useProgram(prog)
    g.uniformMatrix4fv(loc.u_matrix, false, pd.mainMatrix as Float32Array)
    g.uniform4fv(loc.u_clip, lastClip)
    g.uniform1i(loc.u_height, heightCode())
    g.uniform3fv(loc.u_cam, lastCam)
    g.uniform1f(loc.u_flat, isFlat() ? 1 : 0)
    g.uniform1i(loc.u_n, n)
    const ages = new Float32Array(K + 1)
    for (let s = 0; s < K; s++) {
      const t = engine.slotTime(s)
      ages[s] = t === t ? Math.max(0, (engine.time - t) / 1000) : 1e9
    }
    ages[K] = 0
    g.uniform1fv(loc.u_ages, ages)
    g.uniform1f(loc.u_span, K * TRAIL_STEP_S)
    g.uniform1f(loc.u_px, 4.5 * (window.devicePixelRatio || 1))
    // The sources' pulse: seconds, wrapped so float precision holds.
    g.uniform1f(loc.u_time, (performance.now() % 100_000) / 1000)
    g.activeTexture(g.TEXTURE0)
    g.bindTexture(g.TEXTURE_2D, tex)
    g.uniform1i(loc.u_style, 0)
    g.disable(g.DEPTH_TEST)
    g.enable(g.BLEND)
    g.blendFunc(g.ONE, g.ONE_MINUS_SRC_ALPHA)

    g.bindVertexArray(vao)
    if (trails) {
      g.uniform1i(loc.u_mode, 1)
      g.bindBuffer(g.ELEMENT_ARRAY_BUFFER, trailIdx)
      g.drawElements(g.LINES, K * n * 2, g.UNSIGNED_INT, 0)
      g.bindBuffer(g.ELEMENT_ARRAY_BUFFER, headIdx)
      g.drawElements(g.LINES, n * 2, g.UNSIGNED_INT, 0)
      // Leave the VAO's own element buffer bound to it.
      g.bindBuffer(g.ELEMENT_ARRAY_BUFFER, trailIdx)
    }
    if (trackCount) {
      g.bindVertexArray(trackVao)
      g.uniform1i(loc.u_mode, 2)
      g.uniform4fv(loc.u_track, [1, 1, 1, 0.9])
      g.drawElements(g.LINES, trackCount, g.UNSIGNED_SHORT, 0)
      g.bindVertexArray(vao)
    }
    uploadFootprints(g)
    if (fovCount) {
      // What the sources see: fuchsia, on the ground, seen through the globe's own horizon clip.
      g.bindVertexArray(fovVao)
      g.uniform1i(loc.u_mode, 2)
      g.uniform4fv(loc.u_track, [1, 0, 0.8, 0.55])
      g.drawArrays(g.LINES, 0, fovCount)
      g.bindVertexArray(vao)
    }
    g.uniform1i(loc.u_mode, 0)
    g.drawArrays(g.POINTS, K * n, n)
    g.bindVertexArray(null)
  }

  function pick(m: MapLibreMap, x: number, y: number, radiusPx: number): number | null {
    if (!shown() || !engine.count) return null
    const cur = engine.current
    let best: number | null = null
    let bestD = radiusPx * radiusPx
    for (let i = 0; i < engine.count; i++) {
      if (!engine.mask[i]) continue
      const px = cur[i * 3]
      if (px !== px) continue
      // Where the shader draws it, with the same hiding.
      const p = screenOfPoint(m, px, cur[i * 3 + 1], cur[i * 3 + 2])
      if (!p) continue
      const d = (p.x - x) ** 2 + (p.y - y) ** 2
      if (d < bestD) {
        bestD = d
        best = i
      }
    }
    return best
  }

  return impl
}
